/**
 * @file ScheduledMeetingsPage.tsx
 * @brief Scheduled Meetings: the phone's My Meetings list on the web.
 *
 * @details
 * DES (30-09-2026): "this page should match what we have in the app". Port
 * of mobile `MyMeetingsScreen.tsx`:
 * - Title "Scheduled Meetings", "{n} upcoming" under it; no stat boxes.
 * - Two sections, "Upcoming" (soonest first) and "Past & cancelled" (newest
 *   first), in the section-heading look.
 * - Rows, no cards and no dividers: a number ("1.") beside a 20px bold
 *   title; the countdown (amber) and status; the full date; the agenda
 *   note(s); an avatar cluster with "Organised by …" and "N internal · M
 *   external"; languages and length. Past rows are dimmed.
 * - A round red mic starts an upcoming meeting in one click. Clicking the
 *   row opens its actions as a menu (Start now, Edit meeting / Reschedule,
 *   Cancel meeting), the phone's sheet in the web's "⋯" menu look.
 * - Empty: the calendar empty state and "Go to boardroom setup".
 * "Not held" = still scheduled but its time has passed (nobody recorded
 * from it), derived here as on the phone.
 *
 * @author DES
 * @date 28-07-2026
 * @updated 29-07-2026 — GridBlock flush list + FormSheet reschedule
 * @updated 30-09-2026 — the phone's list, rows and actions menu
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  differenceInHours,
  differenceInMinutes,
  format,
  isAfter,
  isToday,
  isTomorrow,
} from "date-fns";
import {
  ArrowLeft,
  CirclePlus,
  CircleX,
  Languages,
  Mic,
  SquarePen,
} from "lucide-react";
import { AgendaFlag } from "@/components/icons/AgendaFlag";
import { EmptyState } from "@/components/feedback/EmptyState";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import type { BoardroomDraftParticipant } from "./capture/boardroomDraftStore";
import { EditScheduledMeetingSheet } from "./EditScheduledMeetingSheet";
import {
  cancelScheduledMeeting,
  listScheduledMeetings,
  type ScheduledMeeting,
  type ScheduledMeetingParticipant,
} from "./scheduledMeetingsApi";
import { useBoardroomBasePath } from "./useBoardroomBasePath";
import { shownEmail, sideCounts, startAgenda } from "./scheduledSide";
import { newestFirst } from "./scheduleFlow";
import { Skeleton } from "@/components/ui/Skeleton";
import { useLoadingSkeleton } from "@/shared/hooks/useLoadingSkeleton";

const MAX_AVATARS = 4;

function participantsFromScheduled(
  m: ScheduledMeeting,
): BoardroomDraftParticipant[] {
  return (m.participants ?? []).map((p) => ({
    id: p.userId || p.id || undefined,
    name: (p.name || shownEmail(p.email) || "Guest").trim(),
    // B48: no stand-in address (DES 02-11-2026); a stored one from an older
    // build is passed on as is, so the recorder still tells guests apart.
    email: p.email || "",
    isExternal: !!p.isExternal,
    shared: p.shared !== false,
    profileImageUrl: p.profileImageUrl ?? undefined,
  }));
}

function getInitials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** The roster, else the organiser alone (the phone's fallback). */
function meetingParticipants(m: ScheduledMeeting): ScheduledMeetingParticipant[] {
  // B45/B48: someone whose access was taken back stays on the roster for the
  // record but is not shown or counted.
  const people = (m.participants ?? []).filter((p) => p.accessRemoved !== true);
  if (people.length > 0) return people;
  return m.organizer_name ? [{ name: m.organizer_name, isExternal: false }] : [];
}

/** "In 12 min", "In 3 hr", "Today · 4:00 PM", "Tomorrow · …", else the date. */
function formatCountdown(when: Date): string {
  const now = new Date();
  const mins = differenceInMinutes(when, now);
  if (mins >= 0 && mins < 60) return `In ${mins} min`;
  const hours = differenceInHours(when, now);
  if (hours >= 0 && hours < 24) return `In ${hours} hr`;
  if (isToday(when)) return `Today · ${format(when, "h:mm a")}`;
  if (isTomorrow(when)) return `Tomorrow · ${format(when, "h:mm a")}`;
  return format(when, "EEE, d MMM · h:mm a");
}

function isNotHeld(m: ScheduledMeeting): boolean {
  // B48: being recorded now (a live session is open) is not "Not held".
  if (m.edit_phase === "during" || m.edit_phase === "after") return false;
  return m.status === "scheduled" && !isAfter(new Date(m.scheduled_at), new Date());
}

function isStartable(m: ScheduledMeeting): boolean {
  return m.status === "scheduled" && isAfter(new Date(m.scheduled_at), new Date());
}

/**
 * B48 (DES 02-11-2026): the organizer edits before, during and after the
 * meeting; only a cancelled one is locked. An older server without
 * `edit_phase` keeps the old rule (scheduled only).
 */
function canEdit(m: ScheduledMeeting): boolean {
  if (!m.is_organizer || m.status === "cancelled") return false;
  return m.edit_phase ? m.edit_phase !== "cancelled" : m.status === "scheduled";
}

/** A row with nothing to do is not clickable (the phone's rule). */
function hasActions(m: ScheduledMeeting): boolean {
  return isStartable(m) || canEdit(m) || (!!m.is_organizer && (m.status === "started" || isNotHeld(m)));
}

const STATUS: Record<string, { label: string; className: string }> = {
  not_held: { label: "Not held", className: "text-destructive" },
  scheduled: { label: "Scheduled", className: "text-warning" },
  started: { label: "In progress", className: "text-brand" },
  completed: { label: "Completed", className: "text-success" },
  cancelled: { label: "Cancelled", className: "text-app-text-secondary" },
};

/** Agenda note titles; only the organiser gets them back from the API. */
function agendaTitles(m: ScheduledMeeting): string | null {
  const x = m as ScheduledMeeting & {
    prep_note_id?: string | null;
    prep_note_title?: string | null;
    prep_notes?: { title?: string | null }[];
  };
  if (!x.prep_note_id && !x.prep_notes?.length) return null;
  return x.prep_notes?.length
    ? x.prep_notes.map((n) => n.title || "Agenda note").join(", ")
    : x.prep_note_title || "Agenda note";
}

/** A photo when it is a real URL and loads; initials otherwise. A raw
 *  storage key ("profile-images/…") has no public URL on the web (the
 *  phone streams it with its token), so it shows initials, not a broken
 *  image. */
function AvatarFace({ url, name }: { url?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  const usable = !!url && /^https?:\/\//.test(url) && !failed;
  return usable ? (
    <img src={url!} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} className="h-full w-full object-cover" />
  ) : (
    <>{getInitials(name)}</>
  );
}

/**
 * Loading placeholder in MeetingRowBody's shape: number, title, status,
 * date, agenda and an avatar row. Web-only skeleton (DES 01-10-2026).
 */
function ScheduledListSkeleton({ rows = 3 }: { rows?: number }) {
  const bone = (cls: string) => <Skeleton still className={cls} />;
  return (
    <div role="status" aria-label="Loading meetings" className="max-w-4xl animate-pulse">
      {bone("mb-1 mt-6 h-5 w-24")}
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-start gap-2 py-4">
          {bone("mt-1 h-5 w-6")}
          <div className="flex min-w-0 flex-1 flex-col gap-2.5 pt-1">
            {bone("h-5 w-3/5")}
            {bone("h-4 w-24")}
            {bone("h-4 w-52")}
            {bone("h-4 w-40")}
            <div className="mt-1 flex">
              {[0, 1, 2].map((a) => (
                <Skeleton
                  key={a}
                 
                  still
                  className={cn("h-[38px] w-[38px] rounded-full border-2 border-app-page", a > 0 && "-ml-2.5")}
                />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MeetingRowBody({ m, number }: { m: ScheduledMeeting; number: number }) {
  const when = new Date(m.scheduled_at);
  const startable = isStartable(m);
  const status =
    STATUS[
      isNotHeld(m)
        ? "not_held"
        : m.status === "scheduled" && m.edit_phase === "during"
          ? "started"
          : m.status
    ] ?? STATUS.cancelled!;
  const people = meetingParticipants(m);
  const { internal, external } = sideCounts(people);
  const langs = m.target_languages ?? [];
  const extras = [
    langs.length > 0
      ? `${langs.slice(0, 2).join(", ")}${langs.length > 2 ? ` +${langs.length - 2}` : ""}`
      : null,
    m.duration_minutes ? `${m.duration_minutes} min` : null,
  ].filter(Boolean);
  const agenda = agendaTitles(m);

  return (
    <>
      <span className="min-w-8 text-xl font-bold leading-7 text-app-text-secondary">
        {number}.
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="line-clamp-2 text-xl font-bold leading-7 text-app-text">
          {m.title?.trim() || "Untitled boardroom"}
        </span>
        <span className="truncate text-[15px] font-semibold">
          {startable ? (
            <span className="text-warning">{formatCountdown(when)} · </span>
          ) : null}
          <span className={status.className}>{status.label}</span>
        </span>
        <span className="text-[15px] text-app-text-secondary">
          {format(when, "EEEE, d MMMM · h:mm a")}
        </span>
        {agenda ? (
          <span className="flex items-center gap-1.5 text-[15px] text-app-text-secondary">
            {/* DES 08-10-2026: app icon, was ListChecks */}
            <AgendaFlag className="h-[15px] w-[15px] shrink-0" />
            <span className="truncate">{agenda}</span>
          </span>
        ) : null}
        {people.length > 0 ? (
          <span className="mt-1.5 flex items-center gap-2">
            <span className="flex shrink-0 items-center">
              {people.slice(0, MAX_AVATARS).map((p, i) => {
                const name = p.name || shownEmail(p.email) || "Guest";
                return (
                  <span
                    key={p.id || p.userId || p.email || `${name}-${i}`}
                    className={cn(
                      "flex h-[38px] w-[38px] items-center justify-center overflow-hidden rounded-full border-2 border-app-page text-[13px] font-bold text-app-text",
                      p.isExternal ? "bg-brand/15" : "bg-app-quaternary",
                      i > 0 && "-ml-3",
                    )}
                  >
                    <AvatarFace url={p.profileImageUrl} name={name} />
                  </span>
                );
              })}
              {people.length > MAX_AVATARS ? (
                <span className="-ml-3 flex h-[38px] w-[38px] items-center justify-center rounded-full border-2 border-app-page bg-app-quaternary-dark text-[13px] font-bold text-app-text">
                  +{people.length - MAX_AVATARS}
                </span>
              ) : null}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5 text-[15px] text-app-text-secondary">
              <span className="truncate">
                {m.is_organizer ? "Organised by you" : `Organised by ${m.organizer_name || "host"}`}
              </span>
              {/* Internal = the organiser + their company; everyone else is
                  external, VERBLIZR user or not (scheduledSide.ts). */}
              <span className="truncate">
                <span className="font-semibold text-app-text">{internal}</span>
                {" internal · "}
                <span className="font-semibold text-app-text">{external}</span>
                {" external"}
              </span>
            </span>
          </span>
        ) : null}
        {extras.length > 0 ? (
          <span className="flex items-center gap-1.5 text-[15px] text-app-text-secondary">
            <Languages className="h-[15px] w-[15px] shrink-0" />
            <span className="truncate">{extras.join(" · ")}</span>
          </span>
        ) : null}
      </span>
    </>
  );
}

function MeetingRow({
  m,
  number,
  onStart,
  onEdit,
  onCancel,
  highlighted = false,
}: {
  m: ScheduledMeeting;
  number: number;
  onStart: (m: ScheduledMeeting) => void;
  onEdit: (m: ScheduledMeeting) => void;
  onCancel: (m: ScheduledMeeting) => void;
  /** Just scheduled (scheduleFlow.ts): a brief tint, as the phone. */
  highlighted?: boolean;
}) {
  const startable = isStartable(m);
  const live = m.status === "started";
  const rowClass = cn(
    "flex min-w-0 flex-1 items-start rounded-2xl py-[18px] text-left transition-colors duration-700",
    !startable && !live && "opacity-70",
    highlighted ? "-mx-3 bg-brand-soft px-3" : "",
  );

  return (
    <div className="flex items-center gap-2">
      {hasActions(m) ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(rowClass, "transition-opacity hover:opacity-80 data-[state=open]:opacity-60")}
            >
              <MeetingRowBody m={m} number={number} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 p-1.5">
            <DropdownMenuLabel className="normal-case tracking-normal">
              <span className="block truncate text-sm font-semibold text-foreground">
                {m.title}
              </span>
              <span className="block text-xs font-normal">
                {format(new Date(m.scheduled_at), "EEEE, d MMMM · h:mm a")}
              </span>
            </DropdownMenuLabel>
            {startable ? (
              <DropdownMenuItem onSelect={() => onStart(m)}>
                <Mic className="h-4 w-4" />
                Start now
              </DropdownMenuItem>
            ) : null}
            {canEdit(m) ? (
              <DropdownMenuItem onSelect={() => onEdit(m)}>
                <SquarePen className="h-4 w-4" />
                {isNotHeld(m) && (m.edit_phase ?? "before") === "before" ? "Reschedule" : "Edit meeting"}
              </DropdownMenuItem>
            ) : null}
            {m.is_organizer && m.status !== "cancelled" && m.status !== "completed" ? (
              <DropdownMenuItem destructive onSelect={() => onCancel(m)}>
                <CircleX className="h-4 w-4" />
                Cancel meeting
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className={rowClass}>
          <MeetingRowBody m={m} number={number} />
        </div>
      )}
      {startable ? (
        <button
          type="button"
          onClick={() => onStart(m)}
          aria-label={`Start ${m.title} now`}
          title="Start now"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand/90"
        >
          <Mic className="h-5 w-5" />
        </button>
      ) : null}
    </div>
  );
}

export function ScheduledMeetingsPage() {
  const base = useBoardroomBasePath();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<ScheduledMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  // A reload after an edit or cancel keeps the list; only the first fetch
  // (nothing on screen yet) shows the skeleton.
  const firstLoad = loading && meetings.length === 0;
  const listSkeleton = useLoadingSkeleton(
    "meetings.scheduled",
    firstLoad,
    loading ? undefined : meetings.length,
  );
  const [editing, setEditing] = useState<ScheduledMeeting | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ScheduledMeeting | null>(
    null,
  );
  const [cancelling, setCancelling] = useState(false);

  // Arrived from "Schedule" (scheduleFlow.ts, DES 10-10-2026): the new meeting
  // on top, tinted for a moment (the toast was shown by the schedule form).
  // The history state is cleared so a refresh or Back+Forward shows nothing.
  const location = useLocation();
  const [highlightId] = useState<string | null>(
    () => (location.state as { highlightId?: string } | null)?.highlightId ?? null,
  );
  const [highlightOn, setHighlightOn] = useState(Boolean(highlightId));
  useEffect(() => {
    if (!highlightId) return;
    navigate(location.pathname + location.search, { replace: true, state: null });
    const t = window.setTimeout(() => setHighlightOn(false), 2500);
    return () => window.clearTimeout(t);
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setMeetings(await listScheduledMeetings("all"));
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't load scheduled meetings",
        description:
          err instanceof ApiClientError
            ? err.message
            : "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const up: ScheduledMeeting[] = [];
    const pa: ScheduledMeeting[] = [];
    for (const m of meetings) {
      const when = new Date(m.scheduled_at);
      if (
        m.status !== "cancelled" &&
        m.status !== "completed" &&
        isAfter(when, now)
      ) {
        up.push(m);
      } else {
        pa.push(m);
      }
    }
    up.sort(
      (a, b) =>
        new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime(),
    );
    pa.sort(
      (a, b) =>
        new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime(),
    );
    return { upcoming: newestFirst(up, highlightId), past: pa };
  }, [meetings, highlightId]);

  // GDPR Art 13 (06-10-2026): a scheduled meeting has no setup screen, so
  // the recording notice is confirmed in a dialog first.
  const [startTarget, setStartTarget] = useState<ScheduledMeeting | null>(null);
  const onStart = (m: ScheduledMeeting) => setStartTarget(m);

  const startConfirmed = (m: ScheduledMeeting) => {
    setStartTarget(null);
    navigate(`${base}/meetings/record`, {
      state: {
        title: m.title,
        targetLanguages:
          m.target_languages?.length > 0 ? m.target_languages : ["en-GB"],
        participants: participantsFromScheduled(m),
        scheduledMeetingId: m.id,
        recordingNotice: { method: "scheduled_confirm", at: new Date().toISOString() },
        // The organiser's agenda rides into the recorder, as on the phone
        // (BoardroomRecorderPage links it once the meeting exists). 10-10-2026.
        ...startAgenda(m),
      },
    });
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelScheduledMeeting(cancelTarget.id);
      toast({ title: "Meeting cancelled" });
      setCancelTarget(null);
      await reload();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Cancel failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setCancelling(false);
    }
  };

  const renderSection = (title: string, list: ScheduledMeeting[]) => (
    <section>
      <h2 className="mb-1 mt-6 text-lg font-semibold tracking-[-0.5px] text-app-text/40">
        {title}
      </h2>
      {list.map((m, i) => (
        <MeetingRow
          key={m.id}
          m={m}
          number={i + 1}
          onStart={onStart}
          onEdit={setEditing}
          onCancel={setCancelTarget}
          highlighted={highlightOn && m.id === highlightId}
        />
      ))}
    </section>
  );

  return (
    <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8">
      <Link
        to={`${base}/meetings`}
        className="-ml-1 inline-flex w-fit items-center gap-1.5 rounded-md px-1 py-0.5 text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Boardrooms
      </Link>
      <h1 className="mt-3 text-[22px] font-bold text-app-text">Scheduled Meetings</h1>
      {!loading && upcoming.length > 0 ? (
        <p className="text-[15px] text-app-text-secondary">{upcoming.length} upcoming</p>
      ) : null}

      {/* First load: skeleton rows if this list had meetings last time, else
          blank, then the meetings or the empty state. */}
      {firstLoad ? (
        listSkeleton ? <ScheduledListSkeleton /> : null
      ) : upcoming.length === 0 && past.length === 0 ? (
        <EmptyState
          shape="calendar"
          title="No scheduled meetings"
          description="Plan a boardroom from the setup screen, or start one anytime without scheduling."
          action={
            <Link
              to={`${base}/meetings/new`}
              className="inline-flex h-12 items-center gap-2 rounded-full bg-brand px-6 text-[15px] font-semibold text-white transition-colors hover:bg-brand/90"
            >
              <CirclePlus className="h-5 w-5" />
              Go to boardroom setup
            </Link>
          }
        />
      ) : (
        <div className="max-w-4xl">
          {upcoming.length > 0 ? renderSection("Upcoming", upcoming) : null}
          {past.length > 0 ? renderSection("Past & cancelled", past) : null}
        </div>
      )}

      {/* B48: title, people and agenda before / during / after; the time
          before only (EditScheduledMeetingSheet). Was time-only. */}
      <EditScheduledMeetingSheet
        open={Boolean(editing)}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
        meeting={editing}
        onSaved={() => {
          setEditing(null);
          void reload();
        }}
      />

      <AlertDialog
        open={Boolean(startTarget)}
        onOpenChange={(open) => !open && setStartTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This meeting will be recorded</AlertDialogTitle>
            <AlertDialogDescription>
              Before you start, make sure everyone in the meeting knows it is being recorded and
              transcribed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-brand text-white hover:bg-brand/90"
              onClick={() => startTarget && startConfirmed(startTarget)}
            >
              Start recording
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(cancelTarget)}
        onOpenChange={(open) => !open && setCancelTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget
                ? `Cancel “${cancelTarget.title}”? Participants will no longer see it as upcoming.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelling}
              onClick={(e) => {
                e.preventDefault();
                void confirmCancel();
              }}
            >
              {cancelling ? "Cancelling…" : "Cancel meeting"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
